import Button from '@/components/UI/Button';
import Card from '@/components/UI/Card';

interface OAuthConnectionStatusProps {
    isProcessing: boolean;
    hasSucceeded: boolean;
    accountAccessWarning: string | null;
    errorMessage: string | null;
}

export default function OAuthConnectionStatus({
    isProcessing,
    hasSucceeded,
    accountAccessWarning,
    errorMessage
}: OAuthConnectionStatusProps) {
    const resolvedAccountAccessWarning = hasSucceeded ? accountAccessWarning : null;
    const statusTitle = isProcessing
        ? 'Connecting your brokerage account'
        : hasSucceeded
          ? resolvedAccountAccessWarning
              ? 'Authorization saved; account action needed'
              : 'Your brokerage authorization is connected'
          : 'We could not complete the connection';

    const statusBody = isProcessing
        ? 'Please wait while Project314 confirms the authorization and finishes your connection.'
        : hasSucceeded
          ? (resolvedAccountAccessWarning ??
            'Authorization was completed successfully. You can continue to import your trading account or review your connected brokerage settings.')
          : (errorMessage ?? 'The brokerage connection could not be completed.');

    return (
        <div className="mx-auto w-full max-w-4xl px-4 py-8 sm:px-6 lg:px-8">
            <div className="mb-8">
                <p className="text-sm font-medium uppercase tracking-[0.22em] text-zinc-500">Brokerage Connection</p>
                <h1 className="mt-3 text-4xl font-semibold text-white">{statusTitle}</h1>
                <p className="mt-4 max-w-3xl text-base leading-7 text-zinc-400">{statusBody}</p>
            </div>

            <div className="grid gap-6 lg:grid-cols-[1.35fr_0.9fr]">
                <Card
                    bodyClassName="space-y-6"
                    subtitle="Project314 uses this step to finalize authorization with your brokerage provider and prepare the account for import."
                    title="Connection status"
                >
                    <div
                        className={`rounded-lg border px-4 py-3 text-sm ${
                            isProcessing
                                ? 'border-sky-500/30 bg-sky-500/10 text-sky-200'
                                : hasSucceeded
                                  ? resolvedAccountAccessWarning
                                      ? 'border-amber-500/30 bg-amber-500/10 text-amber-200'
                                      : 'border-emerald-500/30 bg-emerald-500/10 text-emerald-200'
                                  : 'border-rose-500/30 bg-rose-500/10 text-rose-200'
                        }`}
                    >
                        {isProcessing
                            ? 'Finishing authorization…'
                            : hasSucceeded
                              ? resolvedAccountAccessWarning
                                  ? 'Authorization is saved. Your linked trading accounts need attention before trading can resume.'
                                  : 'Authorization completed successfully.'
                              : statusBody}
                    </div>

                    <div className="flex flex-col gap-3 sm:flex-row">
                        {hasSucceeded ? (
                            <>
                                <Button size="lg" to="/user/tradingAccounts">
                                    Import Trading Account
                                </Button>
                                {resolvedAccountAccessWarning && (
                                    <Button size="lg" to="/user/portfolios" variant="outline-dark">
                                        Review Portfolios
                                    </Button>
                                )}
                                <Button size="lg" to="/user/thirdPartyConnections" variant="outline-dark">
                                    Manage Connections
                                </Button>
                            </>
                        ) : (
                            <>
                                <Button size="lg" to="/connect/brokerage">
                                    Back to Connect Page
                                </Button>
                                <Button size="lg" to="/user/thirdPartyConnections" variant="outline-dark">
                                    Manage Connections
                                </Button>
                            </>
                        )}
                    </div>
                </Card>

                <div className="space-y-6">
                    <Card title="What happens next">
                        <ul className="space-y-3 text-sm leading-6 text-zinc-400">
                            <li className="flex items-start gap-3">
                                <span className="mt-2 h-2 w-2 rounded-full bg-p314-blue" />
                                <span>Import the brokerage account you want to use inside Project314.</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="mt-2 h-2 w-2 rounded-full bg-p314-blue" />
                                <span>Select the intended trading account in each portfolio’s settings and set its allocations.</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="mt-2 h-2 w-2 rounded-full bg-p314-blue" />
                                <span>Review balances, positions, and account access before resuming trading.</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="mt-2 h-2 w-2 rounded-full bg-p314-blue" />
                                <span>Return to the connections page at any time if you need to reconnect authorization.</span>
                            </li>
                        </ul>
                    </Card>

                    <Card title="Security note">
                        <p className="text-sm leading-6 text-zinc-400">
                            Authorization is completed with your brokerage provider. Project314 does not ask for your brokerage password on
                            this page.
                        </p>
                    </Card>
                </div>
            </div>
        </div>
    );
}
